/**
 * Phase 5.4.c — client-side `<selected_range>` / `<current_document>`
 * preamble builder.
 *
 * Plan §7.1 specifies the exact shape the LLM should see when the
 * user sends a prompt with an active selection:
 *
 *   <current_document doc_id="..." version="..." status="...">
 *   {full markdown}
 *   </current_document>
 *
 *   <selected_range start_line="42" end_line="48">
 *   {the selected text verbatim}
 *   </selected_range>
 *
 *   User: {the user's prompt}
 *
 * The plan's ideal delivery path is a server-side `before_prompt_build`
 * hook reshape of a typed `chat.send` attachment. The gateway core's
 * attachment machinery (`src/gateway/chat-attachments.ts`) currently
 * only extracts image attachments — non-image payloads (e.g. a
 * `petition-selection` JSON) are dropped before the hook sees them.
 * Modifying core is out of scope (plugin-only architecture), so 5.4.c
 * ships the preamble inline in the user message. The chat transcript
 * will show the verbose preamble; a follow-up phase can filter it in
 * `ChatMessageList` rendering, or switch to server-side reshape once
 * core extends `chat-attachments.ts` to pass non-image attachments
 * through to the hook.
 *
 * Truncation: the `<current_document>` block is size-bounded so very
 * long petitions don't blow the context window. When the markdown
 * exceeds `MAX_DOCUMENT_CHARS`, we emit a truncation marker and let
 * the agent call `lx_read` on the primary file for the full body.
 */

import type { DocumentSelection } from "@/stores/useDocSelectionStore";

/** Max characters the `<current_document>` block is allowed to carry
 *  inline. ≈15k chars ≈ 3-4k tokens — plan §7.1's guidance. Longer
 *  docs get a truncation marker + the agent re-reads via lx_read. */
const MAX_DOCUMENT_CHARS = 15_000;

export interface PreambleInput {
  /** User's raw prompt text. */
  message: string;
  /** Selection at the time of send. The caller must only pass a
   *  selection that matches `documentMeta.docId` and whose
   *  `versionHash === documentMeta.currentVersionHash` — stale or
   *  cross-doc selections should be filtered out before invoking
   *  this builder (see `AIEditorPromptBox.handleSend`). */
  selection: DocumentSelection | null;
  /**
   * Full markdown body of the current head, or `null` when the body is
   * UNKNOWN — nothing read yet, or the read failed.
   *
   * 🚨 `null` and `""` are NOT interchangeable and the difference is
   * load-bearing. `""` says "this document is empty", which invites the model
   * to write from scratch; `null` says "I could not read it", which must send
   * the model to look before it writes. Passing `content ?? ""` here — the
   * shape this parameter had until 2026-09-10 — announced a 553-word analysis
   * to an LLM as `<!-- empty document; no prior content -->`, one step away
   * from overwriting a lawyer's own work with no sign anything was there.
   */
  documentContent: string | null;
  /**
   * Sidecar meta used to label the `<current_document>` tag. Only a MANAGED
   * document (a sidecar with a real `docId`, a case-scoped UUID — never a path)
   * gets `doc_id` / `version` / `status` / `document_type`. Anything else is
   * labelled by its path: those fields used to be synthesized (`docId = path`,
   * `"petition"`, `"DRAFT"`) and the model read them as facts (mission-writer
   * finding 2026-09-11). `managed: null` = the read failed, so no claim either way.
   * `managed` omitted = inferred from a non-blank `docId` (callers that predate
   * the field); `lib/document-meta.ts` always sets it.
   */
  documentMeta: {
    docId: string | null;
    status: string | null;
    documentType: string | null;
    currentVersionHash: string | null;
    managed?: boolean | null;
  };
  /** Zone-relative docPath for the truncation escape hint (e.g.
   *  `07_drafts/petition-abc.md`, `09_contributions/u-42/notes.md`).
   *  Plan §4.2 — zones vary per documentType, so the marker must
   *  reflect the actual path rather than hardcoding `07_drafts`. */
  docPath: string;
}

/** Build the rewritten message the client actually sends to `chat.send`.
 *  Returns the raw prompt when no AIEditor context is available — the
 *  preamble only applies inside a scoped doc session. */
export function buildAIEditorPreamble(input: PreambleInput): string {
  const { message, selection, documentContent, documentMeta, docPath } = input;
  const blocks: string[] = [];

  const truncatedContent =
    documentContent !== null && documentContent.length > MAX_DOCUMENT_CHARS
      ? `${documentContent.slice(0, MAX_DOCUMENT_CHARS)}\n<!-- truncated — use lx_read on ${docPath} for the full body -->`
      : documentContent;

  const isManaged =
    documentMeta.managed !== false &&
    documentMeta.managed !== null &&
    typeof documentMeta.docId === "string" &&
    documentMeta.docId.trim().length > 0;

  // The `doc_id=` lead is kept on EVERY shape: `PREAMBLE_LEAD` (below) is what
  // `ProseBlock` strips on, and an unmanaged file still needs stripping.
  const currentDocOpenTag = isManaged
    ? `<current_document ` +
      `doc_id="${escapeAttr(documentMeta.docId as string)}" ` +
      `version="${escapeAttr(documentMeta.currentVersionHash ?? "none")}"` +
      (documentMeta.status ? ` status="${escapeAttr(documentMeta.status)}"` : "") +
      (documentMeta.documentType
        ? ` document_type="${escapeAttr(documentMeta.documentType)}"`
        : "") +
      `>`
    : `<current_document doc_id="" path="${escapeAttr(docPath)}"` +
      (documentMeta.managed === false ? ` managed="false"` : "") +
      `>`;
  // Said only when the read PROVED there is no sidecar; a failed read
  // (`managed: null`) makes no claim either way.
  const unmanagedNote =
    documentMeta.managed === false
      ? [
          `<!-- not a managed document: it has no doc_id, version, status or type — refer to it by its path -->`,
        ]
      : [];

  if (documentContent === null) {
    // UNKNOWN — say so, and send the model to the authoritative source. The
    // one thing this branch must never do is fall through to the "empty"
    // wording: an unread document and an empty one look identical here, and
    // only one of them is safe to overwrite.
    blocks.push(
      [
        currentDocOpenTag,
        ...unmanagedNote,
        `<!-- content unavailable — it could NOT be read, which is not the same as empty.`,
        `     Do NOT assume this document is blank and do NOT rewrite it from scratch.`,
        `     Use lx_read on ${docPath} to obtain the current body first. -->`,
        "</current_document>",
      ].join("\n"),
    );
  } else if (documentContent.trim().length === 0) {
    blocks.push(
      [
        currentDocOpenTag,
        ...unmanagedNote,
        "<!-- empty document; no prior content -->",
        "</current_document>",
      ].join("\n"),
    );
  } else {
    blocks.push(
      [currentDocOpenTag, ...unmanagedNote, truncatedContent, "</current_document>"].join("\n"),
    );
  }

  if (selection && selection.text.trim().length > 0) {
    // Defensive: if the selection text contains a literal
    // `</selected_range>` fragment (unlikely but possible when the
    // source markdown itself embeds XML-ish content), the close tag
    // would prematurely end the block from the LLM's view. Escape
    // such closers by splitting the string — zero visual change to
    // the LLM since the opening `<` is still there, and the content
    // remains readable.
    const safeText = selection.text.replace(
      /<\/selected_range>/gi,
      "</selected_ range>",
    );
    blocks.push(
      [
        `<selected_range start_line="${selection.startLine}" end_line="${selection.endLine}">`,
        safeText,
        `</selected_range>`,
      ].join("\n"),
    );
  }

  blocks.push(`${PREAMBLE_USER_MARKER}${message}`);
  return blocks.join("\n\n");
}

/** Escape XML attribute-value special characters. Attributes only
 *  need `&` and `"` escaped (the quote used to delimit the value);
 *  `<` and `>` are legal in attribute content. Kept narrow to avoid
 *  noisy LLM-visible `&lt;` sequences when doc titles contain
 *  angle-brackets. */
function escapeAttr(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

/** The literal prefix `buildAIEditorPreamble` emits before the
 *  user's raw prompt. Load-bearing: `ChatMessageList.displayContent`
 *  splits the rendered message on this marker to strip the inline
 *  preamble from the user bubble. Change here + there in lockstep. */
export const PREAMBLE_USER_MARKER = "User: ";

/** Marker prefix used by `ChatMessageList` to detect a message whose
 *  visible content should be narrowed to just the user prompt part.
 *  Keeps the history display readable while the wire-level payload
 *  still carries the full preamble. Matching on the full opening
 *  tag (with `doc_id=` attribute) avoids false positives if the
 *  user ever starts a prompt with the literal string
 *  `<current_document>` by accident. */
export const PREAMBLE_LEAD = "<current_document doc_id=";
